import React, { useState } from 'react';
import { useEnquiry } from '../../hooks/useEnquiry';
import { Button } from '../common/Button';
import './EnquiryForm.css';

interface Enquiry {
  name: string;
  email: string;
  phone: string;
  company?: string;
  enquiryType: 'tour' | 'membership' | 'enterprise' | 'general';
  message: string;
}

interface EnquiryFormProps {
  onSubmit: (enquiry: Enquiry) => void;
  onClose: () => void;
}

export const EnquiryForm: React.FC<EnquiryFormProps> = ({ onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    enquiryType: 'general' as Enquiry['enquiryType'],
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const { submitEnquiry, isLoading, error } = useEnquiry();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const success = await submitEnquiry({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      company: formData.company,
      enquiryType: formData.enquiryType,
      message: formData.message
    });
    
    if (success) {
      setSubmitted(true);
      setTimeout(() => onClose(), 3000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="enquiry-modal">
      <div className="enquiry-form">
        <div className="form-header">
          <h2>Get in Touch</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="text"
              name="name"
              placeholder="Full Name *"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <input
              type="email"
              name="email"
              placeholder="Email Address *"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number *"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <input
              type="text"
              name="company"
              placeholder="Company (Optional)"
              value={formData.company}
              onChange={handleChange}
            />
          </div>
          
          <div className="form-group">
            <select
              name="enquiryType"
              value={formData.enquiryType}
              onChange={handleChange}
              required
            >
              <option value="general">General Enquiry</option>
              <option value="tour">Book a Tour</option>
              <option value="membership">Membership Plans</option>
              <option value="enterprise">Enterprise Solutions</option>
            </select>
          </div>
          
          <div className="form-group">
            <textarea
              name="message"
              placeholder="Your Message *"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              required
            />
          </div>
          
          {error && (
            <div className="error-message" style={{
              color: '#e74c3c',
              backgroundColor: '#fdf2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '16px',
              textAlign: 'center',
              fontWeight: '500'
            }}>
              {error}
            </div>
          )}
          
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send Enquiry'}
          </Button>
          
          {submitted && (
            <div className="success-message" style={{
              color: '#27ae60',
              backgroundColor: '#f0f9f4',
              border: '1px solid #a7f3d0',
              borderRadius: '6px',
              padding: '12px',
              marginTop: '16px',
              textAlign: 'center',
              fontWeight: '500'
            }}>
              ✅ Thank you! We've sent a confirmation email and will get back to you within 24 hours.
            </div>
          )}
        </form>
      </div>
    </div>
  );
};